import { useWidgetContext } from '@ptek/widget-core';
import { AmbientScribeContext, AutoExportConfig } from '../types';
import { FeatureFlags } from '../types/featureFlags';

/**
 * Feature flag names derived from FeatureFlags type
 * This ensures type safety and auto-completion
 */
export type FeatureFlagName = keyof FeatureFlags;

/**
 * Pure utility function to check feature flag from features object
 * Useful for non-React contexts or when you already have the features object
 */
export const isFeatureEnabled = (
  features: FeatureFlags | undefined,
  flagName: FeatureFlagName
): boolean => {
  return Boolean(features?.[flagName]);
};

/**
 * Each flag the host sent, as the boolean `isFeatureEnabled` reads, so the
 * flags the API records on a generation are exactly the ones the widget acted on.
 */
export const featureFlagStates = (
  features: FeatureFlags | undefined
): FeatureFlags | undefined =>
  features &&
  Object.fromEntries(
    Object.keys(features).map((name) => [
      name,
      isFeatureEnabled(features, name as FeatureFlagName),
    ])
  );

/**
 * React hook to check if a feature flag is enabled
 * Automatically reads from widget context
 *
 * During HMR in development, context might be temporarily unavailable.
 * Defaults to true for DOCUMENT_CONTEXT to maintain expected behavior.
 */
export const hasFeature = (flagName: FeatureFlagName): boolean => {
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();

  const features = widgetContext.context?.features;

  if (!features && process.env.NODE_ENV === 'development') {
    if (flagName === 'documentContext') {
      return true;
    }
  }

  return isFeatureEnabled(features, flagName);
};

/**
 * React hook to get auto-export configuration from widget context.
 * Returns the config if autoExportAfterGeneration is true, null otherwise.
 *
 * The config itself acts as the feature flag - passing autoExportConfig
 * with autoExportAfterGeneration: true opts the brand into this functionality.
 */
export const useAutoExportConfig = (): AutoExportConfig | null => {
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();
  const autoExportConfig = widgetContext.context?.autoExportConfig;

  if (autoExportConfig?.autoExportAfterGeneration) {
    return autoExportConfig;
  }

  return null;
};

/**
 * Pure utility function to get auto-export configuration from context.
 * Returns the config if autoExportAfterGeneration is true, null otherwise.
 *
 * The config itself acts as the feature flag - passing autoExportConfig
 * with autoExportAfterGeneration: true opts the brand into this functionality.
 */
export const getAutoExportConfigFromContext = (
  context: AmbientScribeContext | undefined
): AutoExportConfig | null => {
  const autoExportConfig = context?.autoExportConfig;

  if (autoExportConfig?.autoExportAfterGeneration) {
    return autoExportConfig;
  }

  return null;
};

export const DEFAULT_NOTES_TAB_LABEL = 'SOAP Notes';

/** Trims the override and falls back to the default for missing or blank values. */
export const getNotesTabLabelFromContext = (
  context: AmbientScribeContext | undefined
): string => {
  const raw = context?.notesTabLabel;

  if (typeof raw !== 'string') {
    return DEFAULT_NOTES_TAB_LABEL;
  }

  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : DEFAULT_NOTES_TAB_LABEL;
};

export const useNotesTabLabel = (): string => {
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();
  return getNotesTabLabelFromContext(widgetContext.context);
};

export const DEFAULT_UPDATE_NOTE_BUTTON_LABEL = 'Update SOAP Note';

/** Trims the override and falls back to the default for missing or blank values. */
export const getUpdateNoteButtonLabelFromContext = (
  context: AmbientScribeContext | undefined
): string => {
  const raw = context?.updateNoteButtonLabel;

  if (typeof raw !== 'string') {
    return DEFAULT_UPDATE_NOTE_BUTTON_LABEL;
  }

  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : DEFAULT_UPDATE_NOTE_BUTTON_LABEL;
};

export const useUpdateNoteButtonLabel = (): string => {
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();
  return getUpdateNoteButtonLabelFromContext(widgetContext.context);
};
